import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { Mui } from "@leap4/frontend";
import { darkTheme, lightTheme, ThemeMode } from "./portalTheme";

const { ThemeProvider, CssBaseline } = Mui;

// Same key and 0/1 encoding Leap's useFavoriteTheme wrote, so existing users keep their choice.
const STORAGE_KEY = "themeIdx";

const readStoredMode = (): ThemeMode => {
    try {
        return localStorage.getItem(STORAGE_KEY) === "1" ? "dark" : "light";
    } catch {
        return "light";
    }
};

interface IThemeModeContext {
    mode: ThemeMode;
    toggleMode: () => void;
}

const ThemeModeContext = createContext<IThemeModeContext | null>(null);

export const useThemeMode = (): IThemeModeContext => {
    const ctx = useContext(ThemeModeContext);
    if (!ctx) throw new Error("useThemeMode must be used inside ThemeModeProvider");
    return ctx;
};

export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
    const [mode, setMode] = useState<ThemeMode>(readStoredMode);

    const toggleMode = useCallback(() => {
        setMode((prev) => {
            const next = prev === "light" ? "dark" : "light";
            try {
                localStorage.setItem(STORAGE_KEY, next === "dark" ? "1" : "0");
            } catch {
                // Storage can be blocked; the toggle still works for this visit.
            }
            return next;
        });
    }, []);

    const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

    return (
        <ThemeModeContext.Provider value={value}>
            <ThemeProvider theme={mode === "dark" ? darkTheme : lightTheme}>
                <CssBaseline enableColorScheme />
                {children}
            </ThemeProvider>
        </ThemeModeContext.Provider>
    );
};
